/**
 * Brain Assessment Widget
 *
 * Embedded feature widget for the QultureUp CoPilot assessment flow. The quiz
 * is the SERVER's: it starts an attempt (the server picks and records the
 * questions), every answer is graded and recorded against it, and the results
 * screen draws the placement the server derives — score, level and the
 * missions it skips. The widget grades nothing and places nothing.
 *
 * CoPilot Guide config feature key: "brain_assessment"
 *
 * Config shape:
 * {
 *   worldId: string;              // World to assess (e.g. "local-culture")
 *   questionsPerWorld?: number;   // 3–7, default 5
 * }
 *
 * onComplete payload — the attempt the guide finishes with; the server places
 * it at finalize:
 * {
 *   worldId: string;
 *   attemptId: string;
 * }
 */

import type { WidgetProps } from '@tonnie/copilot';
import {
  QULTUREUP_DIFFICULTY_TIER_LABEL_KEY,
  QULTUREUP_TIER_ON_MISSION_SCALE,
} from '@tonnie/heroes';
import { useTranslation, useLocale } from '@tonnie/locale';
import { useErrorCapture } from '@tonnie/observability';
import type { AssessmentAttempt } from '@tonnie/sdk';
import {
  useAnswerAssessmentQuestion,
  useAssessmentPlacement,
  useStartAssessmentAttempt,
} from '@tonnie/sdk';
import { Button, Card, CardContent, cn, Progress } from '@tonnie/ui';
import {
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronRight,
  Image as ImageIcon,
  Lightbulb,
  Loader2,
  RotateCcw,
} from '@tonnie/assets';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { QultureUpDifficultyPill } from '@/components/shared/QultureUpDifficultyPill';
import { useJourneyInstance } from '@/core/contexts/JourneyInstanceContext';
import { useWidgetState } from '@/features/platform/copilot/flow-lifecycle';

// ─── Deterministic shuffle — same order per question, different across questions

function seededShuffle<T>(arr: T[], seed: number): T[] {
  const shuffled = [...arr];
  let s = Math.abs(seed) || 1;
  for (let i = shuffled.length - 1; i > 0; i--) {
    s = (s * 16807) % 2147483647;
    const j = s % (i + 1);
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

// ─── Types ────────────────────────────────────────────────────────────────────

interface BrainAssessmentConfig {
  worldId: string;
  questionsPerWorld?: number;
}

type QuestionAnswer = {
  questionId: number;
  missionId: string;
  questionTitle: string;
  questionType: string;
  answerId?: number;
  answerText?: string;
  isCorrect: boolean;
  feedback?: string;
  correctAnswer?: string;
};

type Phase = 'loading' | 'question' | 'evaluating' | 'feedback' | 'results' | 'error';

// ─── Component ────────────────────────────────────────────────────────────────

type BrainAssessmentWidgetProps = WidgetProps & {
  config?: BrainAssessmentConfig;
};

// SF-038/SF-019: open-answer character bounds — the minimum is enforced (submit
// stays disabled until reached) and the maximum is hard-capped on the <textarea>.
// The CheckUp motivational form already exposes its own counter; this covers the
// QultureUp assessment open-answer field.
const OPEN_ANSWER_MIN_CHARS = 50;
const OPEN_ANSWER_MAX_CHARS = 528;

export function BrainAssessmentWidget({ config, onComplete }: BrainAssessmentWidgetProps) {
  const { formatPercent } = useLocale();
  const { t } = useTranslation();
  const questionTextId = useId();
  const openAnswerHintId = useId();
  const { captureException } = useErrorCapture();
  const worldId = config?.worldId ?? '';
  const questionsPerWorld = config?.questionsPerWorld ?? 5;

  // The guide's journey — the one the page finalizes on, so the attempt is
  // served, answered and placed on the same journey the result is saved to.
  const { activeInstance } = useJourneyInstance();
  const journeyId = activeInstance?.id;
  const { mutateAsync: startAttempt } = useStartAssessmentAttempt(journeyId);
  const answerQuestion = useAnswerAssessmentQuestion(journeyId);

  const [phase, setPhase] = useState<Phase>('loading');
  // Persistent assessment progress — the ATTEMPT (the questions the server
  // served), currentIndex and cumulative answers are the user's actual progress
  // and MUST survive cross-device resume and tab close. The attempt is persisted
  // with them: a resumed quiz answers the same questions it was served, where
  // re-fetching a question set paired the saved answers with different ones.
  const [persisted, patchPersisted] = useWidgetState<{
    attempt: AssessmentAttempt | null;
    currentIndex: number;
    answers: QuestionAnswer[];
  }>('brain_assessment', { attempt: null, currentIndex: 0, answers: [] });
  const attempt = persisted.attempt;
  const currentIndex = persisted.currentIndex;
  const answers = persisted.answers;
  const setCurrentIndex = useCallback(
    (next: number | ((prev: number) => number)) =>
      patchPersisted({
        currentIndex: typeof next === 'function' ? next(persisted.currentIndex) : next,
      }),
    [patchPersisted, persisted.currentIndex]
  );
  const setAnswers = useCallback(
    (next: QuestionAnswer[] | ((prev: QuestionAnswer[]) => QuestionAnswer[])) =>
      patchPersisted({
        answers: typeof next === 'function' ? next(persisted.answers) : next,
      }),
    [patchPersisted, persisted.answers]
  );
  // Per-question UI ephemerality (current selection in the picker, the
  // open-answer textarea contents, the in-flight feedback bubble) is
  // deliberately NOT persisted — it'd resurrect a stale answer that the
  // user already submitted. Engine variables capture the result.
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(null);
  const [openAnswerText, setOpenAnswerText] = useState('');
  const [feedbackResult, setFeedbackResult] = useState<{
    isCorrect: boolean;
    feedback?: string;
    correctAnswer?: string;
  } | null>(null);

  const [showDetails, setShowDetails] = useState(false);
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());
  const feedbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const questions = useMemo(() => attempt?.questions ?? [], [attempt?.questions]);
  const currentQuestion = questions[currentIndex];
  const shuffledOptions = useMemo(
    () => seededShuffle(currentQuestion?.possibleAnswers ?? [], currentQuestion?.questionId ?? 0),
    [currentQuestion?.possibleAnswers, currentQuestion?.questionId]
  );
  const progressPercent =
    questions.length > 0 ? Math.round((answers.length / questions.length) * 100) : 0;

  // Start the attempt once — unless a resumed quiz already carries one. The
  // result is taken from the mutateAsync PROMISE, never from per-call
  // `mutate(…, { onSuccess })` callbacks: TanStack drops those when the
  // observer unsubscribes mid-flight (StrictMode's effect double-run, a guide
  // re-render remounting the widget), which left the attempt created on the
  // server and the widget on its loading card forever.
  const startedRef = useRef(false);
  useEffect(() => {
    if (attempt || !worldId || !journeyId || startedRef.current) return;
    startedRef.current = true;
    startAttempt({ worldId, questionsPerWorld })
      .then((started) => patchPersisted({ attempt: started, currentIndex: 0, answers: [] }))
      .catch((err: unknown) => {
        captureException(err instanceof Error ? err : new Error(String(err)), {
          component: 'BrainAssessmentWidget',
          action: 'startAttempt',
        });
        setPhase('error');
      });
  }, [
    attempt,
    worldId,
    journeyId,
    questionsPerWorld,
    startAttempt,
    patchPersisted,
    captureException,
  ]);

  // Leave the loading phase once the attempt is here — at the results when a
  // resumed quiz had answered every question.
  useEffect(() => {
    if (phase !== 'loading' || !attempt || questions.length === 0) return;
    setPhase(answers.length >= questions.length ? 'results' : 'question');
  }, [phase, attempt, questions.length, answers.length]);

  // The placement the server derived from this attempt's recorded answers.
  const placementQuery = useAssessmentPlacement(
    phase === 'results' ? (attempt?.attemptId ?? null) : null,
    journeyId
  );

  // Clean up feedback timer on unmount
  useEffect(() => {
    return () => {
      if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    };
  }, []);

  const results = useMemo(() => {
    const placed = placementQuery.data;
    if (!placed) return null;
    const worlds = Object.values(placed.levelPerWorld);
    return {
      level: placed.level,
      score: placed.levelScore,
      correct: worlds.reduce((sum, w) => sum + w.correct, 0),
      total: worlds.reduce((sum, w) => sum + w.total, 0),
      skipMissionIds: worlds.flatMap((w) => w.skipMissionIds),
    };
  }, [placementQuery.data]);

  const pendingNextRef = useRef<number | null>(null);

  const advanceToNext = useCallback(() => {
    const nextIndex = pendingNextRef.current;
    if (nextIndex === null) return;
    pendingNextRef.current = null;
    setFeedbackResult(null);
    if (nextIndex >= questions.length) {
      setPhase('results');
    } else {
      setCurrentIndex(nextIndex);
      setPhase('question');
    }
  }, [questions.length, setCurrentIndex]);

  const advanceAfterFeedback = useCallback(
    (_recordedAnswers: QuestionAnswer[], nextIndex: number, wasCorrect: boolean) => {
      pendingNextRef.current = nextIndex;
      if (wasCorrect) {
        feedbackTimerRef.current = setTimeout(advanceToNext, 1600);
      }
    },
    [advanceToNext]
  );

  const handleSingleChoiceSelect = useCallback((answerId: number) => {
    setSelectedAnswerId(answerId);
  }, []);

  const handleSubmitAnswer = useCallback(async () => {
    if (!currentQuestion || !attempt) return;

    const isSingleChoice =
      currentQuestion.type === 'SINGLE_CHOICE' || currentQuestion.type === 'SINGLE_IMAGE_CHOICE';
    const isOpenAnswer = currentQuestion.type === 'OPEN_ANSWER';

    if (isSingleChoice && selectedAnswerId === null) return;
    if (isOpenAnswer && !openAnswerText.trim()) return;

    setPhase('evaluating');

    // The server's verdict — an answer it could not record is not right here
    // either, as it is not in the placement.
    let isCorrect = false;
    let feedback: string | undefined;
    let correctAnswer: string | undefined;

    try {
      const evalResult = await answerQuestion.mutateAsync({
        attemptId: attempt.attemptId,
        questionId: currentQuestion.questionId,
        answerId: isSingleChoice ? (selectedAnswerId ?? undefined) : undefined,
        answerText: isOpenAnswer ? openAnswerText : undefined,
      });
      isCorrect = evalResult?.isCorrect ?? false;
      feedback = evalResult?.feedback;
      correctAnswer = evalResult?.correctAnswer;
    } catch (err) {
      captureException(err instanceof Error ? err : new Error(String(err)), {
        component: 'BrainAssessmentWidget',
        action: 'evaluateAnswer',
      });
    }

    const answer: QuestionAnswer = {
      questionId: currentQuestion.questionId,
      missionId: currentQuestion.missionId,
      questionTitle: currentQuestion.question ?? currentQuestion.title,
      questionType: currentQuestion.type,
      answerId: isSingleChoice ? (selectedAnswerId ?? undefined) : undefined,
      answerText: isOpenAnswer ? openAnswerText : undefined,
      isCorrect,
      feedback,
      correctAnswer,
    };

    const newAnswers = [...answers, answer];
    setAnswers(newAnswers);
    setSelectedAnswerId(null);
    setOpenAnswerText('');
    setFeedbackResult({ isCorrect, feedback, correctAnswer });
    setPhase('feedback');

    advanceAfterFeedback(newAnswers, currentIndex + 1, isCorrect);
  }, [
    currentQuestion,
    selectedAnswerId,
    openAnswerText,
    answers,
    currentIndex,
    attempt,
    answerQuestion,
    advanceAfterFeedback,
    setAnswers,
    captureException,
  ]);

  const handleComplete = useCallback(() => {
    if (!attempt || !results) return;
    onComplete?.({ worldId, attemptId: attempt.attemptId });
  }, [attempt, results, worldId, onComplete]);

  const handleRetake = useCallback(() => {
    if (feedbackTimerRef.current) clearTimeout(feedbackTimerRef.current);
    setSelectedAnswerId(null);
    setOpenAnswerText('');
    setFeedbackResult(null);
    setShowDetails(false);
    // A retake is a new attempt — the placed one stays as it was.
    patchPersisted({ attempt: null, currentIndex: 0, answers: [] });
    startedRef.current = false;
    setPhase('loading');
  }, [patchPersisted]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (phase === 'loading' || (phase === 'results' && placementQuery.isLoading)) {
    return (
      <Card
        className="border-border/50 shadow-sm"
        data-testid="widget-brain_assessment"
        data-state="loading"
      >
        <CardContent className="pt-6 flex flex-col items-center gap-3 py-8">
          <Loader2 className="w-8 h-8 text-primary-accessible animate-spin" />
          <p className="text-sm text-muted-foreground">{t('copilot.brainAssessment.loading')}</p>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'error' || placementQuery.isError || (attempt && questions.length === 0)) {
    return (
      <Card
        className="border-border/50 shadow-sm"
        data-testid="widget-brain_assessment"
        data-state="error"
      >
        <CardContent className="pt-6 flex flex-col items-center gap-3 py-8">
          <AlertCircle className="w-8 h-8 text-destructive-accessible" />
          <p className="text-sm font-medium text-foreground">
            {t('copilot.brainAssessment.errorTitle')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('copilot.brainAssessment.noQuestions')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'results' && results) {
    const levelConfig = {
      beginner: {
        color: 'text-tone-violet',
        bg: 'bg-tone-violet-subtle',
        label: t('copilot.brainAssessment.level.beginner.label'),
        icon: '🌱',
        desc: t('copilot.brainAssessment.level.beginner.desc'),
      },
      intermediate: {
        color: 'text-tone-blue',
        bg: 'bg-tone-blue-subtle',
        label: t('copilot.brainAssessment.level.intermediate.label'),
        icon: '📈',
        desc: t('copilot.brainAssessment.level.intermediate.desc', {
          count: results.skipMissionIds.length,
        }),
      },
      advanced: {
        color: 'text-tone-green',
        bg: 'bg-tone-green-subtle',
        label: t('copilot.brainAssessment.level.advanced.label'),
        icon: '🏆',
        desc: t('copilot.brainAssessment.level.advanced.desc', {
          count: results.skipMissionIds.length,
        }),
      },
    };
    const cfg = levelConfig[results.level];

    return (
      <Card
        className="border-border/50 shadow-sm overflow-hidden"
        data-testid="widget-brain_assessment-level-result"
      >
        <CardContent className="pt-6 space-y-4">
          <div className={cn('rounded-xl p-4 text-center', cfg.bg)}>
            <div className="text-3xl mb-2">{cfg.icon}</div>
            <h3 className={cn('text-xl font-bold', cfg.color)}>{cfg.label}</h3>
            <p className="text-sm text-muted-foreground mt-1">{cfg.desc}</p>
          </div>

          <div className="flex items-center justify-between p-3 rounded-lg bg-muted/50">
            <span className="text-sm text-muted-foreground">
              {t('copilot.brainAssessment.results.score')}
            </span>
            <div className="flex items-center gap-2">
              <Progress
                aria-label={t('copilot.brainAssessment.results.score')}
                value={results.score}
                className="w-24 h-2"
              />
              <span className="text-sm font-semibold">{formatPercent(results.score)}</span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-center">
            <div className="p-2 rounded-lg bg-tone-green-subtle">
              <p className="text-lg font-bold text-tone-green">{results.correct}</p>
              <p className="text-xs text-muted-foreground">
                {t('copilot.brainAssessment.results.correct')}
              </p>
            </div>
            <div className="p-2 rounded-lg bg-muted/50">
              <p className="text-lg font-bold text-foreground">{results.total}</p>
              <p className="text-xs text-muted-foreground">
                {t('copilot.brainAssessment.results.total')}
              </p>
            </div>
          </div>

          {results.skipMissionIds.length > 0 && (
            <div className="p-3 rounded-lg border border-border bg-muted/30">
              <p className="text-xs text-muted-foreground">
                {t('copilot.brainAssessment.results.skipNote', {
                  count: results.skipMissionIds.length,
                })}
              </p>
            </div>
          )}

          {/* Per-question detail panel */}
          <div className="border border-border rounded-lg overflow-hidden">
            <button
              onClick={() => setShowDetails((v) => !v)}
              className="flex items-center justify-between w-full px-3 py-2.5 text-xs font-medium text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <span>{t('copilot.brainAssessment.results.questionDetails')}</span>
              <ChevronDown
                className={cn('w-3.5 h-3.5 transition-transform', showDetails && 'rotate-180')}
              />
            </button>
            {showDetails && (
              <div className="border-t divide-y divide-border">
                {answers.map((ans, idx) => (
                  <div key={ans.questionId} className="px-3 py-2.5 space-y-1.5">
                    <div className="flex items-start gap-2">
                      <span className="text-2xs font-bold text-muted-foreground mt-0.5 shrink-0">
                        Q{idx + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-medium text-foreground leading-snug">
                          {ans.questionTitle}
                        </p>
                        {ans.answerText && (
                          <p className="text-2xs text-muted-foreground mt-1">
                            <span className="font-medium">
                              {t('copilot.brainAssessment.results.yourAnswer')}:
                            </span>{' '}
                            {ans.answerText}
                          </p>
                        )}
                        {ans.feedback && (
                          <p className="text-2xs text-muted-foreground mt-0.5 italic">
                            {ans.feedback}
                          </p>
                        )}
                        {!ans.isCorrect && ans.correctAnswer && (
                          // Recap parity with the live feedback card —
                          // amber pill + "The answer is" label, not the
                          // pre-2026-04-27 blue "Correct answer:" pill.
                          <div className="mt-1.5 rounded bg-tone-amber-subtle border border-tone-amber-line px-2 py-1">
                            <p className="text-2xs text-tone-amber">
                              <span className="font-medium">
                                {t('copilot.brainAssessment.feedback.theAnswerIs')}:
                              </span>{' '}
                              {ans.correctAnswer}
                            </p>
                          </div>
                        )}
                      </div>
                      {ans.isCorrect ? (
                        <CheckCircle
                          className="w-4 h-4 text-tone-green shrink-0 mt-0.5"
                          aria-hidden
                        />
                      ) : (
                        <Lightbulb
                          className="w-4 h-4 text-tone-amber shrink-0 mt-0.5"
                          aria-hidden
                        />
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex gap-2 pt-2">
            <Button variant="outline" size="sm" onClick={handleRetake} className="flex-1">
              <RotateCcw className="w-4 h-4 mr-1.5" />
              {t('copilot.brainAssessment.results.retake')}
            </Button>
            <Button
              size="sm"
              onClick={handleComplete}
              data-testid="widget-brain_assessment-continue"
              className="flex-1 bg-tone-violet-solid hover:bg-tone-violet-solid/90 text-tone-violet-on-solid"
            >
              {t('copilot.brainAssessment.results.continue')}
              <ChevronRight className="w-4 h-4 ml-1.5" />
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'evaluating') {
    return (
      <Card className="border-border/50 shadow-sm">
        <CardContent className="pt-6 flex flex-col items-center gap-3 py-8">
          <Loader2 className="w-8 h-8 text-tone-violet animate-spin" />
          <p className="text-sm font-medium text-foreground">
            {t('copilot.brainAssessment.evaluating.title')}
          </p>
          <p className="text-xs text-muted-foreground">
            {t('copilot.brainAssessment.evaluating.subtitle')}
          </p>
        </CardContent>
      </Card>
    );
  }

  if (phase === 'feedback' && feedbackResult) {
    const isCorrect = feedbackResult.isCorrect;
    return (
      // Wrong-answer state is intentionally NOT framed as a system error.
      // Pre-2026-04-27 it used `border-2 border-red-400` + a red XCircle,
      // which end users read as "the app crashed" — red in our system is
      // reserved for destructive actions. The coaching palette below
      // (amber + soft ring + Lightbulb icon) signals "pay attention,
      // here's what to learn" without triggering the error-state alarm.
      <Card
        className={cn(
          'shadow-sm overflow-hidden transition-all',
          isCorrect
            ? 'border-tone-green-solid bg-tone-green-subtle'
            : 'border-tone-amber-line bg-tone-amber-subtle ring-1 ring-tone-amber-line'
        )}
      >
        <CardContent className="pt-6 flex flex-col items-center gap-3 py-8">
          {/* Icon — green check for correct, amber Lightbulb for incorrect.
              Lightbulb (vs XCircle) reads as "insight incoming", which is
              literally what the answer card below it is. */}
          {isCorrect ? (
            <CheckCircle className="w-12 h-12 text-tone-green" aria-hidden />
          ) : (
            <Lightbulb className="w-12 h-12 text-tone-amber" aria-hidden />
          )}

          {/* Headline + warm subline (incorrect only). The old single-line
              "Not quite…" was curt; pairing it with an encouraging line
              lowers the failure-shame and frames the next screen as a
              learning moment instead of a verdict. */}
          <div className="flex flex-col items-center gap-1">
            <p
              className={cn(
                'text-base font-semibold',
                isCorrect ? 'text-tone-green' : 'text-tone-amber'
              )}
            >
              {isCorrect
                ? t('copilot.brainAssessment.feedback.correct')
                : t('copilot.brainAssessment.feedback.incorrectHeadline')}
            </p>
            {!isCorrect && (
              <p className="text-xs text-muted-foreground text-center max-w-xs">
                {t('copilot.brainAssessment.feedback.incorrectEncouragement')}
              </p>
            )}
          </div>

          {/* AI-generated feedback (when present). Same neutral style for
              both outcomes — it's narrative, not a verdict. */}
          {feedbackResult.feedback && (
            <p className="text-xs text-muted-foreground text-center max-w-xs">
              {feedbackResult.feedback}
            </p>
          )}

          {/* The answer card — the visual hero. Neutral palette so it
              doesn't compete with the amber framing above; small green
              check tags it as the trustworthy take-away. Larger answer
              text (text-sm) than the old blue pill (text-xs) because
              this is the most useful information on the screen. */}
          {!isCorrect && feedbackResult.correctAnswer && (
            <div className="mt-1 w-full max-w-xs rounded-lg border border-border/60 bg-background/80 dark:bg-background/40 px-3.5 py-2.5 shadow-sm">
              <div className="flex items-center gap-1.5 mb-1">
                <CheckCircle className="w-3.5 h-3.5 text-tone-green shrink-0" aria-hidden />
                <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
                  {t('copilot.brainAssessment.feedback.theAnswerIs')}
                </p>
              </div>
              <p className="text-sm font-medium text-foreground">{feedbackResult.correctAnswer}</p>
            </div>
          )}

          {isCorrect ? (
            <p className="text-xs text-muted-foreground mt-1">
              {t('copilot.brainAssessment.feedback.movingOn')}
            </p>
          ) : (
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={advanceToNext}
              data-testid="widget-brain_assessment-feedback-next"
            >
              {t('copilot.brainAssessment.feedback.next')}
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          )}
        </CardContent>
      </Card>
    );
  }

  // Question phase
  if (!currentQuestion) return null;

  const isSingleChoice =
    currentQuestion.type === 'SINGLE_CHOICE' || currentQuestion.type === 'SINGLE_IMAGE_CHOICE';
  const isOpenAnswer = currentQuestion.type === 'OPEN_ANSWER';
  const isImageChoice = currentQuestion.type === 'SINGLE_IMAGE_CHOICE';
  // SF-038/SF-019: open answers must reach the minimum before submit unlocks.
  const openAnswerLen = openAnswerText.trim().length;
  const openAnswerBelowMin = openAnswerLen < OPEN_ANSWER_MIN_CHARS;
  const canSubmit = isSingleChoice
    ? selectedAnswerId !== null
    : openAnswerLen >= OPEN_ANSWER_MIN_CHARS;

  return (
    <Card
      className="border-border/50 shadow-sm overflow-hidden"
      data-testid="widget-brain_assessment"
    >
      <CardContent className="pt-4 space-y-4">
        {/* Progress */}
        <div className="flex items-center gap-3">
          <Progress
            aria-label={t('common.progress')}
            value={progressPercent}
            className="flex-1 h-1.5"
          />
          <span className="text-xs text-muted-foreground shrink-0">
            {answers.length + 1} / {questions.length}
          </span>
        </div>

        {/* Tier — in the reader's language, on the difficulty palette (MOB-27). */}
        <div className="flex items-center gap-2">
          <QultureUpDifficultyPill
            level={QULTUREUP_TIER_ON_MISSION_SCALE[currentQuestion.difficultyTier]}
            label={t(QULTUREUP_DIFFICULTY_TIER_LABEL_KEY[currentQuestion.difficultyTier])}
            testId="widget-brain_assessment-tier"
          />
        </div>

        {/* Question text */}
        <div className="space-y-1" data-testid="widget-brain_assessment-question">
          <p id={questionTextId} className="text-sm font-semibold text-foreground leading-snug">
            {currentQuestion.question ?? currentQuestion.title}
          </p>
        </div>

        {/* Answer options */}
        {isSingleChoice && shuffledOptions.length > 0 && (
          <div className={cn('gap-2', isImageChoice ? 'grid grid-cols-2' : 'flex flex-col')}>
            {shuffledOptions.map((option) => {
              const isSelected = selectedAnswerId === option.id;
              return isImageChoice && option.imageUrl ? (
                <button
                  key={option.id}
                  onClick={() => handleSingleChoiceSelect(option.id)}
                  data-testid={`widget-brain_assessment-answer-${option.id}`}
                  className={cn(
                    'relative flex flex-col rounded-lg border-2 overflow-hidden transition-all',
                    isSelected
                      ? 'border-primary ring-2 ring-primary/30'
                      : 'border-border hover:border-primary/50'
                  )}
                >
                  {/* Image — SF-042: show the FULL image (object-contain) inside
                      a shorter, height-capped box so both options + the prompt
                      fit without cropping/overflow. The caption is rendered
                      IN-FLOW below it (not overlaid) so a long option label
                      stays fully legible instead of being clipped over the art. */}
                  <div className="relative w-full aspect-[4/3] max-h-56 bg-muted">
                    {failedImages.has(option.imageUrl) ? (
                      <div className="absolute inset-0 bg-muted flex items-center justify-center">
                        <ImageIcon className="w-8 h-8 text-muted-foreground" />
                      </div>
                    ) : (
                      <img
                        src={option.imageUrl}
                        alt={option.content}
                        className="w-full h-full object-contain"
                        onError={() =>
                          setFailedImages((prev) => new Set(prev).add(option.imageUrl!))
                        }
                      />
                    )}
                    {isSelected && (
                      <div className="absolute top-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                        <CheckCircle className="w-3 h-3 text-white" />
                      </div>
                    )}
                  </div>
                  {option.content && (
                    <div
                      className={cn(
                        'w-full px-2 py-1.5 text-xs text-center leading-snug line-clamp-2',
                        isSelected
                          ? 'bg-primary/10 text-primary-accessible font-medium'
                          : 'bg-background text-foreground'
                      )}
                    >
                      {option.content}
                    </div>
                  )}
                </button>
              ) : (
                <button
                  key={option.id}
                  onClick={() => handleSingleChoiceSelect(option.id)}
                  data-testid={`widget-brain_assessment-answer-${option.id}`}
                  className={cn(
                    'w-full text-left px-4 py-3 rounded-lg border text-sm transition-all',
                    isSelected
                      ? 'border-primary bg-primary/10 text-primary-accessible font-medium'
                      : 'border-border bg-background hover:bg-muted/50'
                  )}
                >
                  {option.content}
                </button>
              );
            })}
          </div>
        )}

        {isOpenAnswer && (
          <div className="space-y-2">
            <textarea
              aria-labelledby={questionTextId}
              aria-describedby={openAnswerHintId}
              value={openAnswerText}
              onChange={(e) => setOpenAnswerText(e.target.value)}
              data-testid="widget-brain_assessment-open-answer"
              placeholder={t('copilot.brainAssessment.question.openAnswerPlaceholder')}
              rows={3}
              maxLength={OPEN_ANSWER_MAX_CHARS}
              className="w-full px-3 py-2 rounded-lg border border-border bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary/30"
            />
            <p id={openAnswerHintId} className="text-xs text-muted-foreground">
              {t('copilot.brainAssessment.question.openAnswerHint')}
            </p>
            <p
              className={`text-xs ${openAnswerBelowMin ? 'text-tone-amber' : 'text-muted-foreground'}`}
              data-testid="widget-brain_assessment-open-answer-counter"
            >
              {openAnswerBelowMin
                ? t('copilot.brainAssessment.question.openAnswerMinHint', {
                    min: OPEN_ANSWER_MIN_CHARS,
                    remaining: OPEN_ANSWER_MIN_CHARS - openAnswerLen,
                  })
                : t('copilot.brainAssessment.question.openAnswerCounter', {
                    count: openAnswerLen,
                    max: OPEN_ANSWER_MAX_CHARS,
                  })}
            </p>
          </div>
        )}

        {/* Submit button */}
        <Button
          onClick={() => void handleSubmitAnswer()}
          disabled={!canSubmit}
          data-testid="widget-brain_assessment-next"
          className="w-full bg-tone-violet-solid hover:bg-tone-violet-solid/90 text-tone-violet-on-solid"
        >
          {answers.length + 1 === questions.length
            ? t('copilot.brainAssessment.question.submitFinal')
            : t('copilot.brainAssessment.question.next')}
          <ChevronRight className="w-4 h-4 ml-1.5" />
        </Button>
      </CardContent>
    </Card>
  );
}

export default BrainAssessmentWidget;
